import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarbellIcon,
  HeartbeatIcon,
  LightningIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { profilingEndpoints, ITestingProtocol } from "../../endpoints/profiling";
import { Paginated } from "../../typings/Paginated";
import { ConfiguraTestModal } from "./ConfiguraTestModal";
import CustomButton from "../../shared/CustomButton/CustomButton";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import EmptyContainer from "../../shared/EmptyContainer/EmptyContainer";
import AutoPRAlertCard from "./AutoPRAlertCard";
import ProtocolCard from "./ProtocolCard";
import { ProfilingProtocolDetailContent } from "../ProfilingProtocolDetailPage/ProfilingProtocolDetailContent";
import styles from "./ProfilingHubPage.module.scss";

const MVP_ENABLED = new Set([
  "P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8",
  "P9", "P10", "P11", "P12", "P13", "P14",
]);

const CLUSTERS = [
  {
    key: "fv",
    title: "Forza · Velocità",
    Icon: BarbellIcon,
    protocols: ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8"],
  },
  {
    key: "endurance",
    title: "Resistenza",
    Icon: HeartbeatIcon,
    protocols: ["P9", "P10", "P11", "P12", "P13", "P14"],
  },
];

interface Props {
  athleteId: string;
}

export const ProfilingHubContent: React.FC<Props> = ({ athleteId }) => {
  const qc = useQueryClient();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState<string | null>(null);

  const { data: protocolsResp, isLoading } = useQuery({
    queryKey: ["profiling-protocols", "profile"],
    queryFn: () =>
      // `type` non è supportato come filtro server-side da listProtocols
      // (accetta solo `exerciseId`): il campo veniva già ignorato a runtime.
      profilingEndpoints.listProtocols().then((r) => r.data),
  });

  const { data: profilesResp } = useQuery({
    queryKey: ["athlete-profiles", athleteId],
    queryFn: () =>
      profilingEndpoints
        .listAthleteProfiles(athleteId, { limit: 100 })
        .then((r) => r.data),
    enabled: !!athleteId,
  });

  const { data: alertsResp } = useQuery({
    queryKey: ["auto-pr-alerts", athleteId],
    queryFn: () =>
      profilingEndpoints.listAutoPRAlerts(athleteId).then((r) => r.data),
    enabled: !!athleteId,
  });

  const confirmPRMut = useMutation({
    mutationFn: (alertId: string) =>
      profilingEndpoints.confirmAutoPR(alertId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auto-pr-alerts"] });
      qc.invalidateQueries({ queryKey: ["exercise-history"] });
    },
  });

  const discardPRMut = useMutation({
    mutationFn: (alertId: string) =>
      profilingEndpoints.discardAutoPR(alertId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auto-pr-alerts"] });
    },
  });

  const protocols: ITestingProtocol[] =
    (protocolsResp as Paginated<ITestingProtocol> | undefined)?.docs ??
    (protocolsResp as { data?: ITestingProtocol[] } | undefined)?.data ??
    (Array.isArray(protocolsResp) ? protocolsResp : []) ??
    [];
  const profiles = profilesResp ?? [];
  const autoPRAlerts: any[] = alertsResp?.docs ?? [];

  const protocolByHubId = useMemo(() => {
    const m = new Map<string, ITestingProtocol>();
    for (const p of protocols) {
      if (p.profilingHubProtocolId) m.set(p.profilingHubProtocolId, p);
    }
    return m;
  }, [protocols]);

  const lastProfileByProtocolId = useMemo(() => {
    const m = new Map<string, any>();
    for (const p of profiles) {
      const cur = m.get(p.protocolId);
      if (!cur || new Date(p.sessionDate) > new Date(cur.sessionDate)) {
        m.set(p.protocolId, p);
      }
    }
    return m;
  }, [profiles]);

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <LeopartLoader size="lg" />
      </div>
    );
  }

  /* ── Inline detail view ───────────────────────────────────────────────── */
  if (selectedProtocolId) {
    return (
      <ProfilingProtocolDetailContent
        athleteId={athleteId}
        protocolId={selectedProtocolId}
        onBack={() => setSelectedProtocolId(null)}
      />
    );
  }

  const hasAnyProtocol = CLUSTERS.some((c) =>
    c.protocols.some((pid) => protocolByHubId.has(pid))
  );

  if (!hasAnyProtocol && protocols.length === 0) {
    return (
      <>
        <EmptyContainer
          Icon={BarbellIcon}
          title="Nessun protocollo configurato"
          description="Configura il primo test per iniziare a tracciare il profilo atletico"
          buttonText="Configura test"
          onClick={() => setShowConfigModal(true)}
        />
        <ConfiguraTestModal
          open={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          athleteId={athleteId}
        />
      </>
    );
  }

  return (
    <>
      {/* Title row */}
      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <p className={styles.pageTitle}>Profiling Hub</p>
          <p className={styles.pageSubtitle}>
            14 protocolli che generano profilo/curva — tutti P1-P14 compute
            attivo backend
          </p>
        </div>
        <CustomButton
          color="primary"
          size="md"
          iconPosition="left"
          icon={<PlusIcon size={16} weight="bold" />}
          onClick={() => setShowConfigModal(true)}
        >
          Configura nuovo test
        </CustomButton>
      </div>

      {/* Auto-PR alerts */}
      {autoPRAlerts.length > 0 && (
        <div className={styles.alertSection}>
          <div className={styles.alertSectionTitle}>
            <LightningIcon size={16} color="#ffbf18" weight="fill" />
            <p className={styles.alertSectionLabel}>
              {autoPRAlerts.length}{" "}
              {autoPRAlerts.length === 1
                ? "possibile nuovo PR rilevato"
                : "possibili nuovi PR rilevati"}{" "}
              da training
            </p>
          </div>
          {autoPRAlerts.map((a) => (
            <AutoPRAlertCard
              key={a._id}
              alert={a}
              isConfirming={
                confirmPRMut.isPending && confirmPRMut.variables === a._id
              }
              isDiscarding={
                discardPRMut.isPending && discardPRMut.variables === a._id
              }
              onConfirm={(id) => confirmPRMut.mutate(id)}
              onDiscard={(id) => discardPRMut.mutate(id)}
            />
          ))}
        </div>
      )}

      {/* Protocol clusters */}
      <div className={styles.clustersStack}>
        {CLUSTERS.map((cluster) => (
          <section key={cluster.key} className={styles.section}>
            <div className={styles.sectionHeader}>
              <cluster.Icon size={20} color="#00212c" weight="bold" />
              <p className={styles.sectionTitle}>{cluster.title}</p>
            </div>
            <div className={styles.cardGrid}>
              {cluster.protocols.map((protoId) => {
                const protocol = protocolByHubId.get(protoId);
                const isEnabled = MVP_ENABLED.has(protoId);
                const lastProfile = protocol
                  ? lastProfileByProtocolId.get(protocol._id)
                  : undefined;
                return (
                  <ProtocolCard
                    key={protoId}
                    protoId={protoId}
                    protocol={protocol}
                    isEnabled={isEnabled}
                    lastProfile={lastProfile}
                    athleteId={athleteId}
                    onClick={
                      isEnabled && protocol
                        ? (id) => setSelectedProtocolId(id)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <ConfiguraTestModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        athleteId={athleteId}
      />
    </>
  );
};

export default ProfilingHubContent;
